import React, { useCallback, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { downloadDocument } from '../../services/documentService';
import { DocumentKind, resolveForPreview, withExtension } from '../../utils/documentPreview';

/**
 * Viewing a document without leaving the page.
 *
 * Paired with useDocumentPreview so a table only has to say which document to open; the hook
 * fetches it, works out what it is -- the document service sends no usable content type, so that
 * can mean reading the file's own bytes -- and this renders it.
 */

interface PreviewState {
  name: string;
  kind: DocumentKind;
  url: string;
}

export const useDocumentPreview = (onError?: (message: string) => void) => {
  const [preview, setPreview] = useState<PreviewState | null>(null);
  const [openingKey, setOpeningKey] = useState<string | null>(null);

  const open = useCallback(async (documentKey: string, name?: string, contentType?: string) => {
    try {
      setOpeningKey(documentKey);
      const blob = await downloadDocument(documentKey);
      const { kind, blob: typed } = await resolveForPreview(blob, name, contentType);
      setPreview({
        name: name || 'Document',
        kind,
        url: window.URL.createObjectURL(typed)
      });
    } catch (error) {
      console.error('Error opening document:', error);
      onError?.('Could not open the document');
    } finally {
      setOpeningKey(null);
    }
  }, [onError]);

  const close = useCallback(() => {
    setPreview(prev => {
      if (prev) window.URL.revokeObjectURL(prev.url);
      return null;
    });
  }, []);

  return { preview, openingKey, open, close };
};

interface DocumentPreviewModalProps {
  preview: PreviewState | null;
  onClose: () => void;
}

export const DocumentPreviewModal: React.FC<DocumentPreviewModalProps> = ({ preview, onClose }) => (
  <Modal show={!!preview} onHide={onClose} size="xl" centered>
    <Modal.Header closeButton>
      <Modal.Title className="fs-6 text-truncate">{preview?.name}</Modal.Title>
    </Modal.Header>
    <Modal.Body className="p-0" style={{ minHeight: '60vh' }}>
      {preview?.kind === 'pdf' && (
        <iframe
          src={preview.url}
          title={preview.name}
          style={{ width: '100%', height: '75vh', border: 0 }}
        />
      )}
      {preview?.kind === 'image' && (
        <div className="d-flex justify-content-center align-items-center p-3" style={{ minHeight: '60vh' }}>
          <img src={preview.url} alt={preview.name} style={{ maxWidth: '100%', maxHeight: '72vh' }} />
        </div>
      )}
      {preview?.kind === 'other' && (
        <div className="text-center text-muted p-5">
          <i className="bi bi-file-earmark" style={{ fontSize: '2.5rem' }}></i>
          <p className="mt-3 mb-0">This file type cannot be shown here. Download it to open it.</p>
        </div>
      )}
    </Modal.Body>
    <Modal.Footer>
      {preview && (
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={async () => {
            const response = await fetch(preview.url);
            const blob = await response.blob();
            const link = document.createElement('a');
            link.href = preview.url;
            link.download = await withExtension(preview.name, blob);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }}
        >
          <i className="bi bi-download me-1"></i>
          Download
        </Button>
      )}
      <Button variant="secondary" size="sm" onClick={onClose}>Close</Button>
    </Modal.Footer>
  </Modal>
);
